import { ReactNode } from 'react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useDisplayUnits } from '@/hooks/useDisplayUnits';
import {
  buildUomTooltip,
  formatUnitSize,
  formatPackCase,
  gramsToUnit,
  type ListingUomFields,
  type MassUnit,
  type VolumeUnit,
  UNIT_LABEL,
} from '@/lib/uom';

export type TooltipColumn =
  | 'price'
  | 'quantity'
  | 'unitSize'
  | 'caseSize'
  | 'thc'
  | 'cbd'
  | 'terpene';

interface Props {
  listing: ListingUomFields | null | undefined;
  column: TooltipColumn;
  currencySymbol?: string;
  children: ReactNode;
  className?: string;
}

/**
 * Per-column hover tooltip. Each column owns its own explanation, so hovering
 * the price never shows a quantity tooltip and vice-versa. Returns plain
 * children when there's nothing meaningful to add.
 */
export function ColumnTooltip({ listing, column, currencySymbol = '', children, className }: Props) {
  const { tooltipMassUnit, tooltipVolumeUnit } = useDisplayUnits();

  const body = renderBody(column, listing, tooltipMassUnit, tooltipVolumeUnit, currencySymbol);
  if (!body) return <span className={className}>{children}</span>;

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className={`${className ?? ''} cursor-help underline decoration-dotted underline-offset-4 decoration-muted-foreground/40`}>
            {children}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs max-w-[260px]">
          {body}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function renderBody(
  column: TooltipColumn,
  l: ListingUomFields | null | undefined,
  massPref: MassUnit | null,
  volumePref: VolumeUnit | null,
  cur: string
): ReactNode | null {
  if (!l) return null;
  const dim = (l.dimension as string) || 'mass';
  const pref: MassUnit | VolumeUnit | null = dim === 'volume' ? volumePref : massPref;
  switch (column) {
    case 'price': {
      const conv = buildUomTooltip(l, pref, cur);
      if (!conv?.price) return null;
      return (
        <>
          <div className="font-medium text-foreground">In your preferred unit</div>
          <div>≈ {conv.price}</div>
        </>
      );
    }
    case 'quantity': {
      const conv = buildUomTooltip(l, pref, cur);
      if (!conv?.quantity) return null;
      return (
        <>
          <div className="font-medium text-foreground">In your preferred unit</div>
          <div>≈ {conv.quantity}</div>
        </>
      );
    }
    case 'unitSize': {
      const sz = formatUnitSize(l);
      if (!sz) return null;
      const w = l.unit_weight_g ?? null;
      const inPref = w != null && massPref ? gramsToUnit(w, massPref) : null;
      return (
        <>
          <div className="font-medium text-foreground">Per-unit size</div>
          <div>{sz}</div>
          {inPref != null && massPref && massPref !== 'g' && (
            <div className="text-muted-foreground">
              ≈ {inPref.toLocaleString('en-US', { maximumFractionDigits: 4 })} {UNIT_LABEL[massPref]}/unit
            </div>
          )}
        </>
      );
    }
    case 'caseSize': {
      const pc = formatPackCase(l);
      if (!pc) return null;
      const upp = l.units_per_pack ?? null;
      const ppc = l.packs_per_case ?? null;
      const w = l.unit_weight_g ?? null;
      const totalUnits = upp && ppc ? upp * ppc : (l.case_size ?? null);
      const totalG = totalUnits != null && w != null ? totalUnits * w : null;
      return (
        <>
          <div className="font-medium text-foreground">Case packaging</div>
          <div>{pc}</div>
          {totalG != null && (
            <div className="text-muted-foreground">
              ≈ {totalG.toLocaleString('en-US', { maximumFractionDigits: 1 })} g per case
            </div>
          )}
        </>
      );
    }
    case 'thc':
      return (
        <>
          <div className="font-medium text-foreground">THC</div>
          <div className="text-muted-foreground">Total tetrahydrocannabinol on this listing.</div>
        </>
      );
    case 'cbd':
      return (
        <>
          <div className="font-medium text-foreground">CBD</div>
          <div className="text-muted-foreground">Total cannabidiol on this listing.</div>
        </>
      );
    case 'terpene':
      return (
        <>
          <div className="font-medium text-foreground">Terpenes</div>
          <div className="text-muted-foreground">Total terpene content on this listing.</div>
        </>
      );
    default:
      return null;
  }
}
